var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _HznInput_instances, _HznInput_emit, _HznInput_onKeydown, _HznInput_handleInput, _HznInput_handleChange, _HznInput_showClearable_get, _HznInput_toggleSensitiveData, _HznInput_handleSensitiveType, _HznInput_renderIconContainer;
// class definition
import { html, LitElement, nothing } from '@horizon/base';
import { property, state, query } from '@horizon/base/decorators';
import { classMap, ifDefined, live } from '@horizon/base/directives';
import { HasSlotController } from '@horizon/common/controllers';
import { TextInputMixin } from '@horizon/common/mixins';
import { ScopedElementsMixin } from '@open-wc/scoped-elements';
import { submit } from '@open-wc/form-helpers';
import { eventEmitter } from '@horizon/common/events';
import { HznDivider } from '@horizon/divider';
import { HznIconClose, HznIconHide, HznIconShow } from '@horizon/icons/individual';
import { HznInline } from '@horizon/inline';
import { HznStack } from '@horizon/stack';
import { HznText } from '@horizon/text';
import InputStyles from './input.css.js';
export const renderIconSlot = Symbol('_renderIconSlot');
export const hasNoSlottedIcon = Symbol('_hasNoSlottedIcon');
export const handleClear = Symbol('_handleClear');
/**
 *
 * @tag hzn-input
 * @tagname hzn-input
 * @summary A text input component that mirrors a native input
 *
 * @fires {CustomEvent<{}>} input - Emitted every time a character is typed into the input
 * @fires {CustomEvent<{}>} change - Emitted when the input is changed
 * @fires {CustomEvent<{}>} clear - Emitted when the input is clearable and the clear button is clicked
 */
export class HznInput extends TextInputMixin(ScopedElementsMixin(LitElement)) {
    constructor() {
        super(...arguments);
        _HznInput_instances.add(this);
        /**
         * @private
         */
        _HznInput_emit.set(this, eventEmitter(this));
        this.hasSlotController = new HasSlotController(this, '[default]', 'trailing-icon');
        this.sensitiveHidden = false;
        /**
         * Set the native input type
         * @playroomValues {'email' | 'number' | 'password' | 'search' | 'tel' | 'text' | 'url' | 'date'}
         */
        this.type = 'text';
        /**
         * Whether or not the input is compact size
         */
        this.compact = false;
        /**
         * Whether or not the input label is visually hidden
         */
        this.hideLabel = false;
        /**
         * Adds a clear button when the input is populated
         */
        this.clearable = false;
        /**
         * Adds a clickable show/hide icon button that hides or reveals the input contents
         */
        this.sensitive = false;
        /**
         * The input's autofocus attribute
         */
        this.autofocus = false;
    }
    static get scopedElements() {
        return {
            'hzn-stack': HznStack,
            'hzn-text': HznText,
            'hzn-inline': HznInline,
            'hzn-icon-close': HznIconClose,
            'hzn-icon-show': HznIconShow,
            'hzn-icon-hide': HznIconHide,
            'hzn-divider': HznDivider,
        };
    }
    /**
     * Programmatically clear the input
     */
    clear() {
        if (this.innerClearButton) {
            this.innerClearButton.click();
        }
    }
    connectedCallback() {
        super.connectedCallback();
        // automatically hide the input value if sensitive is set by default
        if (this.type === 'password') {
            this.sensitive = true;
        }
        if (this.sensitive) {
            this.sensitiveHidden = true;
        }
        // hide default browser invalid popup
        this.addEventListener('invalid', (event) => event.preventDefault());
    }
    /**
     * @private
     */
    resetFormControl() {
        this.value = '';
    }
    /**
     * @private
     */
    [(_HznInput_emit = new WeakMap(), _HznInput_instances = new WeakSet(), _HznInput_onKeydown = function _HznInput_onKeydown(event) {
        const hasModifier = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
        // Pressing enter when focused on an input should submit the form like a native input
        // use event.key here to detect both 'Enter' and 'NumpadEnter' which both have key === 'Enter'
        if (event.key === 'Enter' && !hasModifier) {
            if (this.form) {
                submit(this.form);
            }
        }
    }, _HznInput_handleInput = function _HznInput_handleInput(event) {
        // dont stop propagation on the native event, because
        // the inputs that extend from hzn-input may be listening and need it
        // also don't emit a custom event, because the native input event is
        // composed and will escape the shadow DOM
        this.value = event.target.value;
    }, _HznInput_handleChange = function _HznInput_handleChange(event) {
        event.stopImmediatePropagation();
        event.preventDefault();
        __classPrivateFieldGet(this, _HznInput_emit, "f").call(this, { type: 'change' });
    }, handleClear)]() {
        this.value = '';
        this.focus();
        __classPrivateFieldGet(this, _HznInput_emit, "f").call(this, { type: 'clear' });
    }
    /**
     * @private
     */
    [(_HznInput_showClearable_get = function _HznInput_showClearable_get() {
        // only show the clear button if clearable, with a value
        // and not disabled or readonly
        return this.clearable && this.value && !this.disabled && !this.readonly;
    }, _HznInput_toggleSensitiveData = function _HznInput_toggleSensitiveData() {
        this.sensitiveHidden = !this.sensitiveHidden;
    }, _HznInput_handleSensitiveType = function _HznInput_handleSensitiveType() {
        if (this.type === 'password') {
            return this.sensitive && this.sensitiveHidden ? this.type : 'text';
        }
        return this.sensitive && this.sensitiveHidden ? 'password' : this.type;
    }, hasNoSlottedIcon)]() {
        return this.sensitive;
    }
    /**
     * @private
     */
    [renderIconSlot]() {
        if (this.sensitive) {
            // if sensitive, never render an icon slot
            return html `<span class="sensitive-toggle-button">
        <button tabindex="-1" @click="${__classPrivateFieldGet(this, _HznInput_instances, "m", _HznInput_toggleSensitiveData)}">
          <span class="visually-hidden">${this.sensitiveHidden ? 'Show' : 'Hide'} sensitive data</span>
          ${this.sensitiveHidden
                ? html `<hzn-icon-show class="toggle-icon" title="Show sensitive data" role="presentation"></hzn-icon-show>`
                : html `<hzn-icon-hide class="toggle-icon" title="Hide sensitive data" role="presentation"></hzn-icon-hide>`}
        </button>
      </span>`;
        }
        return this.hasSlotController.test('trailing-icon')
            ? html ` <span class="trailing-icon" role="presentation">
          <slot name="trailing-icon"></slot>
        </span>`
            : nothing;
    }
    render() {
        const inputContainerStyle = {
            'input-container': true,
            compact: Boolean(this.compact),
            error: this.showErrors,
            'clearable-has-value': Boolean(__classPrivateFieldGet(this, _HznInput_instances, "a", _HznInput_showClearable_get)),
        };
        return html `<hzn-stack space="xsmall">
      <label for="input" class="${this.hideLabel ? 'visually-hidden' : ''}">
        <hzn-text>
          <slot>${this.displayName || this.name || 'Text Input'}</slot>
        </hzn-text>
        ${this.sensitive
            ? html `<span class="visually-hidden"
              >Sensitive data; data in this field can be visually hidden or shown with the toggle button</span
            >`
            : nothing}
      </label>
      <hzn-inline class="${classMap(inputContainerStyle)}" space="xxsmall">
        <input
          title=""
          id="input"
          type=${__classPrivateFieldGet(this, _HznInput_instances, "m", _HznInput_handleSensitiveType).call(this)}
          name=${ifDefined(this.name)}
          ?disabled=${this.disabled}
          ?readonly=${this.readonly}
          ?required=${this.required}
          placeholder=${ifDefined(this.placeholder)}
          minlength=${ifDefined(this.minlength)}
          maxlength=${ifDefined(this.maxlength)}
          min=${ifDefined(this.min)}
          max=${ifDefined(this.max)}
          step=${ifDefined(this.step)}
          .value=${live(this.value)}
          autocapitalize=${ifDefined(this.autocapitalize)}
          autocomplete=${ifDefined(this.autocomplete)}
          autocorrect=${ifDefined(this.autocorrect)}
          ?autofocus=${this.autofocus}
          spellcheck=${ifDefined(this.spellcheck ? this.spellCheck : undefined)}
          pattern=${ifDefined(this.pattern)}
          enterkeyhint=${ifDefined(this.enterkeyhint)}
          inputmode=${ifDefined(this.inputmode)}
          aria-describedby="${ifDefined(this.helperText ? 'helper-text' : undefined)}"
          aria-invalid="${this.showErrors ? 'true' : 'false'}"
          @input="${__classPrivateFieldGet(this, _HznInput_instances, "m", _HznInput_handleInput)}"
          @change="${__classPrivateFieldGet(this, _HznInput_instances, "m", _HznInput_handleChange)}"
          @keydown="${__classPrivateFieldGet(this, _HznInput_instances, "m", _HznInput_onKeydown)}"
        />
        ${__classPrivateFieldGet(this, _HznInput_instances, "m", _HznInput_renderIconContainer).call(this)}
      </hzn-inline>
      ${this.helperText
            ? html ` <hzn-text
            class="${this.hideHelperText ? 'visually-hidden' : ''}"
            id="helper-text"
            variant="caption"
            tone="subdued"
            >${this.helperText}</hzn-text
          >`
            : nothing}
      ${this.showErrors
            ? html `<hzn-text role="alert" id="error-message" class="error-message" variant="caption" tone="critical">
            ${this.internalErrorMessage}
          </hzn-text>`
            : nothing}
    </hzn-stack>`;
    }
}
_HznInput_renderIconContainer = function _HznInput_renderIconContainer() {
    if (__classPrivateFieldGet(this, _HznInput_instances, "a", _HznInput_showClearable_get) || this[hasNoSlottedIcon]() || this.hasSlotController.test('trailing-icon')) {
        return html `<div class="icon-container">
        ${__classPrivateFieldGet(this, _HznInput_instances, "a", _HznInput_showClearable_get)
            ? html `
              <button
                tabindex="-1"
                class="clear-button ${!this[hasNoSlottedIcon]() && !this.hasSlotController.test('trailing-icon')
                ? 'no-icon'
                : ''}"
                @click="${this[handleClear]}"
              >
                <span class="visually-hidden">Clear input value</span>
                <hzn-icon-close class="clear-icon" role="presentation"></hzn-icon-close>
              </button>
              ${this[hasNoSlottedIcon]() || this.hasSlotController.test('trailing-icon')
                ? html `<hzn-divider tone="subdued" vertical class="divider"></hzn-divider>`
                : nothing}
            `
            : nothing}
        ${this[renderIconSlot]()}
      </div>`;
    }
    return '';
};
HznInput.styles = [InputStyles];
__decorate([
    state()
], HznInput.prototype, "sensitiveHidden", void 0);
__decorate([
    query('.clear-button')
], HznInput.prototype, "innerClearButton", void 0);
__decorate([
    property({ type: String, reflect: true })
], HznInput.prototype, "type", void 0);
__decorate([
    property({ type: Boolean })
], HznInput.prototype, "compact", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-label' })
], HznInput.prototype, "hideLabel", void 0);
__decorate([
    property({ type: Boolean })
], HznInput.prototype, "clearable", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HznInput.prototype, "sensitive", void 0);
__decorate([
    property()
], HznInput.prototype, "placeholder", void 0);
__decorate([
    property()
], HznInput.prototype, "autocapitalize", void 0);
__decorate([
    property()
], HznInput.prototype, "autocorrect", void 0);
__decorate([
    property()
], HznInput.prototype, "autocomplete", void 0);
__decorate([
    property({ type: Boolean })
], HznInput.prototype, "autofocus", void 0);
__decorate([
    property()
], HznInput.prototype, "enterkeyhint", void 0);
__decorate([
    property({ type: String, attribute: 'spellcheck' })
], HznInput.prototype, "spellCheck", void 0);
__decorate([
    property()
], HznInput.prototype, "inputmode", void 0);
//# sourceMappingURL=input.js.map